import angular from 'angular';
import csmCommonWeb from 'csm-common-web';

export default angular
  .module('books.util.BooksSettings', [csmCommonWeb])
  .factory('BooksSettings', BooksSettingsFactory).name;

/*****************************************************************************
 * Factory: BooksSettings
 *
 * Contains settings for Books Applications
 *****************************************************************************/
/* @ngInject */
function BooksSettingsFactory(SERVER) {
  /*-------------------*/
  /* Entity Types List */
  /*-------------------*/
  /** Default List of Entities. Flags in the items indicate if the types are for
      use in transactions. */
  var ENTITY_TYPE_LIST = [
    {
      type: SERVER.BooksDBConstants.ET_STUDENT,
      label: 'Student',
      forTx: true
    },
    {
      type: SERVER.BooksDBConstants.ET_TEACHER,
      label: 'Teacher',
      forTx: true
    },
    { type: SERVER.BooksDBConstants.ET_PARENT, label: 'Parent', forTx: true },
    {
      type: SERVER.BooksDBConstants.ET_GRANDPARENT,
      label: 'Grandparent',
      forTx: true
    },
    {
      type: SERVER.BooksDBConstants.ET_COMMUNITY_PARTNER,
      label: 'Community Partner',
      forTx: true
    },
    { type: SERVER.BooksDBConstants.ET_VENDOR, label: 'Vendor', forTx: true },
    { type: SERVER.BooksDBConstants.ET_GROUP, label: 'Group', forTx: false },
    { type: SERVER.BooksDBConstants.ET_CLUB, label: 'Club', forTx: false },
    { type: SERVER.BooksDBConstants.ET_FAMILY, label: 'Family', forTx: true },
    { type: SERVER.BooksDBConstants.ET_OTHER, label: 'Other', forTx: true }
  ];

  /*****************************************************************************
   * addressEntityLabel */
  /**
  * Returns a display string for the provided AddressEntityListItem input. The
  * string returned will be of the format: 'fullName (StudentID)' or 'fullName'
  * when there is no studentID.
  *
  * @param   aeli    AddressEntityListItem input object
  * @return          Display string if input provided, otherwise ''
  *****************************************************************************/
  function addressEntityLabel(aeli, showGrade = false) {
    var output = '';

    if (aeli?.entity) {
      output =
        aeli.entity.name +
        (aeli.entity.studentID ? ' (' + aeli.entity.studentID + ')' : '');

      if (showGrade && angular.isNumber(aeli?.entity?.grade) && aeli.entity.grade >= 0) {
        output += ` [${aeli.entity.grade}]`;
      }
    }

    return output.trim();
  }

  return {
    /*------------------------*/
    /* Database Field Lengths */
    /*------------------------*/
    /** Name Field */ FL_NAME: 100,
    /** Notes Field */ FL_NOTES: 500,

    /*----------------*/
    /* String Formats */
    /*----------------*/
    /** Fundraiser. */ FMT_FNAME_FUND: '{0}_{1}_fund.png',
    /** Home */ FMT_FNAME_HOME: '{0}_home.png',
    /** Logo. */ FMT_FNAME_LOGO: '{0}_logo.png',
    /** Product. */ FMT_FNAME_PRODUCT: '{0}_{1}.png',
    /** Title */ FMT_FNAME_TITLE: '{0}_title.png',

    /***************************************************************************
     * Methods
     ***************************************************************************/
    /*****************************************************************************
     * addressEntityLabel */
    /**
     * Returns a display string for the provided AddressEntityListItem input. The
     * string returned will be of the format: 'fullName (StudentID)' or 'fullName'
     * when there is no studentID.
     *
     * @param   aeli    AddressEntityListItem input object
     * @return          Display string if input provided, otherwise ''
     *****************************************************************************/
    addressEntityLabel,
    /*****************************************************************************
    * addressEntitySearchTargets */
    /**
     * Returns the array of strings that should be searchable for the provided
     * AddressEntityListItem. Searches match by name, fullName, and studentID.
     *****************************************************************************/
    addressEntitySearchTargets: function (aeli) {
      if (!aeli?.entity) return [];
      var targets = [];

      if (aeli.entity.name) targets.push(aeli.entity.name);       // "Lightyear, Buzz"
      if (aeli.entity.fullName) targets.push(aeli.entity.fullName);   // "Buzz Lightyear"
      if (aeli.entity.studentID) targets.push(aeli.entity.studentID);  // "1995"
      return targets;
    },
    /*****************************************************************************
     * entityAddressItemLabel */
    /**
     * Returns a display string for the provided AddressEntityListItem input. The
     * string returned will be of the format: 'fullName (StudentID)' or 'fullName'
     * when there is no studentID.
     *
     * @param   aeli    AddressEntityListItem input object
     * @return          Display string if input provided, otherwise ''
     *****************************************************************************/
    entityAddressItemLabel: function (aeli) {
      var output = '';

      if (angular.isObject(aeli))
        output =
          (aeli.entity && aeli.entity.name ? aeli.entity.name : '') +
          ' ' +
          (aeli.address &&
          aeli.address.address &&
          aeli.address.city &&
          aeli.address.state &&
          aeli.address.zip
            ? '(' +
              aeli.address.address +
              ', ' +
              aeli.address.city +
              ', ' +
              aeli.address.state +
              ' ' +
              aeli.address.zip +
              ')'
            : '');

      return output.trim();
    },
    /**
    * There are three possible scenarios that can result in how the entity value is returned
    * to this function. There are 3 different types of data that can be passed in and for each
    * one we need to handle showing them properly.
    * 
    * 1. Entity is a string that the user typed in, an entity does not exist in our system yet just
    *    display the string.
    * 2. The passed in entity is an object that contains an entity and address object. Use the constructed
    *    label function for the address entity to display the entity information.
    *    Ex: entity: {
    *                  entity: { id: 1, name: 'John Doe', studentID: '12345', etc. },
    *                  address: { id: 153964, address: '123 address street', etc. }
    *                }
    *    
    * 3. The passed in object is just an diret entity object. Construct the label using the name, studentID, and grade if they exist.
    * Ex: entity: { id: 1, name: 'John Doe', studentID: '12345', etc. }
    *
    * @param {string | AddressEntity | Entity} entity
    * @returns {string}
    */
    entityLabel: function (entity, showGrade = false) {
      /**
      * The order of the check is important. We want to make sure we are always prioritizing
      * the newly entered in entity, then the already set entity.
      */
      if (entity) {
        if (typeof entity === 'string') return entity;

        if (entity.entity) return addressEntityLabel(entity, showGrade);

        let entityLabel = entity.name;
        if (entity.studentID) entityLabel += ` (${entity.studentID})`;
        if (entity.grade && entity.grade >= 0 && showGrade) entityLabel += ` [${entity.grade}]`;

        return entityLabel.trim();
      }

      return '';
    },

    /** Entity Type List. If forTxOnly, then return only the entity types
        used with transactions. */
    entityTypes: function (forTxOnly) {
      return ENTITY_TYPE_LIST.filter(function (e) {
        return forTxOnly ? e.forTx : true;
      });
    },

    /** Ledger Entity Types CSV List */
    ledgerEntityTypes: function () {
      return this.entityTypes(true)
        .map(function (ent) {
          return ent.type;
        })
        .join(',');
    }
  };
}
